/**
 * Security & compliance HTTP adapter — `/v1/admin/security/*`, `/v1/admin/audit`,
 * retention, legal hold, eDiscovery export, plus the self-service `/v1/auth/*`
 * session and MFA surfaces.
 *
 * Live-only, like adminApi: a security console that shows fixtures is worse than
 * one that shows an error, because an operator would act on the fiction.
 *
 * THE BINARY CASE: `/admin/audit.csv` answers `text/csv`, which `apiRequest`
 * cannot carry because it parses every body as JSON — so it routes through
 * `authedBlob` below. That is the only hand-rolled fetch here.
 *
 * EXPORT DOWNLOAD IS TWO CALLS, DELIBERATELY. `/admin/exports/:id/download` used
 * to answer a 302 into presigned object storage, and that could not work from a
 * browser: `fetch` follows redirects with the request's headers intact, storage
 * is served from the same origin here so nothing stripped them, and the
 * presigned GET arrived carrying BOTH a SigV4 query signature and our bearer
 * token. S3 answers that with `400 InvalidRequest — request has multiple
 * authentication types`. Every ready export, every user, every time.
 *
 * The endpoint now answers JSON with the signed URL, and the archive is fetched
 * in a second, unauthenticated request. The audit record is written by the first
 * call, so "who took a copy of this data" is still answered.
 */

import type {
  AuditEntry,
  AuditFilters,
  CreateExportRequest,
  ExportJob,
  LegalHold,
  RetentionDataKind,
  RetentionPolicy,
} from '@cyphex/types/admin';
import type { ErrorCode, Uuid } from '@cyphex/types/common';
import type {
  ActiveSession,
  RetentionRun,
  SecurityPolicy,
  SecurityPolicyUpdate,
} from '@cyphex/types/security';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api/client';
import { API_BASE_URL } from '@/lib/api/env';
import { ApiRequestError } from '@/lib/api/errors';
import { query } from '@/lib/api/query';
import { getAccessToken } from '@/lib/api/token';

/* ---------------------------------------------------------------------------
 * Response envelopes only this console reads. Domain models stay in @cyphex/types.
 * ------------------------------------------------------------------------- */

export interface AuditPage {
  entries: AuditEntry[];
  nextCursor: string | null;
}

export interface MfaCoverageUser {
  userId: Uuid;
  email: string;
  role: string;
  mfaEnabled: boolean;
  createdAt: string;
}

export interface MfaCoverage {
  users: MfaCoverageUser[];
  summary: { total: number; enrolled: number; notEnrolled: number };
}

export type RetentionScopeKind = 'tenant' | 'team' | 'channel' | 'conversation';

export interface CreateRetentionPolicyRequest {
  name: string;
  dataKind: RetentionDataKind;
  retainDays: number;
  action?: 'delete' | 'redact';
  scopeKind?: RetentionScopeKind;
  scopeId?: Uuid;
  enabled?: boolean;
}

export interface CreateLegalHoldRequest {
  matterName: string;
  description?: string;
  userIds?: Uuid[];
  teamIds?: Uuid[];
  channelIds?: Uuid[];
  conversationIds?: Uuid[];
  endsAt?: string;
}

export interface MfaEnrolment {
  secret: string;
  uri: string;
  digits: number;
  periodSeconds: number;
}

/** The filter half of the query string. Shared by the paged view and the CSV. */
function auditFilterQuery(filters: AuditFilters): Record<string, string | null> {
  return {
    actorId: filters.actorId ?? null,
    actions: filters.actions === undefined ? null : filters.actions.join(','),
    targetKind: filters.targetKind ?? null,
    targetId: filters.targetId ?? null,
    result: filters.result ?? null,
    from: filters.from ?? null,
    to: filters.to ?? null,
  };
}

/** Paged view: filters plus keyset cursor and page size. */
const auditPageQuery = (filters: AuditFilters, cursor: string | null): string =>
  query({ ...auditFilterQuery(filters), cursor, limit: 100 });

/**
 * CSV export: filters ONLY.
 *
 * Deliberately no `limit` — the server caps the export at AUDIT_CSV_MAX and
 * ignores the parameter, so sending a page size here would read, in an access
 * log, like a 100-row export that in fact returned a hundred thousand.
 */
const auditCsvQuery = (filters: AuditFilters): string => query(auditFilterQuery(filters));

/**
 * GETs a bearer-authenticated endpoint that answers bytes rather than JSON.
 *
 * Deliberately NOT a plain `<a href>`: these endpoints need an Authorization
 * header, which an anchor cannot carry. Fetching here also means a 429 (the
 * export rate limit) or a 400 ("that export is not ready") surfaces as a real
 * error the UI can explain, instead of the browser navigating away to an error
 * body. Redirects are followed normally, so the 302 into object storage
 * resolves to the archive bytes.
 */
async function authedBlob(path: string, accept: string, fallback: string): Promise<Blob> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: accept, ...(token === null ? {} : { Authorization: `Bearer ${token}` }) },
  });

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const envelope =
      typeof payload === 'object' && payload !== null && 'error' in payload
        ? (payload as { error: { code?: ErrorCode; message?: string } }).error
        : null;
    throw new ApiRequestError(response.status, {
      code: envelope?.code ?? 'internal_error',
      message: envelope?.message ?? fallback,
      requestId: response.headers.get('x-request-id') ?? 'unknown',
    });
  }
  return response.blob();
}

export const fetchAuditCsv = (filters: AuditFilters): Promise<Blob> =>
  authedBlob(
    `/admin/audit.csv${auditCsvQuery(filters)}`,
    'text/csv',
    'The audit export could not be produced.',
  );

export interface ExportDownloadLink {
  url: string;
  filename: string;
  checksum: string | null;
  sizeBytes: number | null;
}

export async function fetchExportArchive(exportId: Uuid): Promise<Blob> {
  // 1. Authenticated, audited: ask for a link.
  const link = await apiGet<ExportDownloadLink>(`/admin/exports/${exportId}/download`);
  // 2. Unauthenticated: the presigned URL is the credential, and adding a second
  //    one is precisely what broke this before. No `Authorization`, no cookies.
  const response = await fetch(link.url, { credentials: 'omit' });
  if (!response.ok) {
    throw new ApiRequestError(response.status, {
      code: 'internal_error',
      message: 'The archive link was rejected by storage. Ask for the export again.',
      requestId: response.headers.get('x-request-id') ?? 'unknown',
    });
  }
  return response.blob();
}

export const securityApi = {
  /* ---- Policy ------------------------------------------------------------ */

  getPolicy: (signal?: AbortSignal) => apiGet<SecurityPolicy>('/admin/security/policy', signal),

  updatePolicy: (input: SecurityPolicyUpdate) =>
    apiPatch<Pick<SecurityPolicy, 'password' | 'session' | 'mfa'>>('/admin/security/policy', input),

  /* ---- Sessions (admin view of any user) --------------------------------- */

  listUserSessions: (userId: Uuid, signal?: AbortSignal) =>
    apiGet<{ sessions: ActiveSession[] }>(`/admin/security/users/${userId}/sessions`, signal).then(
      (payload) => payload.sessions ?? [],
    ),

  revokeSession: (sessionId: Uuid) =>
    apiDelete<{ ok: true }>(`/admin/security/sessions/${sessionId}`),

  revokeAllSessions: (userId: Uuid) =>
    apiPost<{ revoked: number }>(`/admin/security/users/${userId}/sessions/revoke-all`),

  requirePasswordChange: (userId: Uuid) =>
    apiPost<{ ok: true }>(`/admin/security/users/${userId}/require-password-change`),

  /* ---- MFA coverage & retention runs ------------------------------------- */

  mfaCoverage: (signal?: AbortSignal) => apiGet<MfaCoverage>('/admin/security/mfa-coverage', signal),

  retentionRuns: (signal?: AbortSignal) =>
    apiGet<{ runs: RetentionRun[] }>('/admin/security/retention-runs', signal).then(
      (payload) => payload.runs ?? [],
    ),

  /* ---- Audit log --------------------------------------------------------- */

  listAudit: (filters: AuditFilters, cursor: string | null, signal?: AbortSignal) =>
    apiGet<AuditPage>(`/admin/audit${auditPageQuery(filters, cursor)}`, signal),

  /* ---- Retention --------------------------------------------------------- */

  listRetentionPolicies: (signal?: AbortSignal) =>
    apiGet<{ policies: RetentionPolicy[] }>('/admin/retention-policies', signal).then(
      (payload) => payload.policies ?? [],
    ),

  createRetentionPolicy: (input: CreateRetentionPolicyRequest) =>
    apiPost<{ id: Uuid }>('/admin/retention-policies', input),

  /* ---- Legal hold -------------------------------------------------------- */

  listLegalHolds: (signal?: AbortSignal) =>
    apiGet<{ holds: LegalHold[] }>('/admin/legal-holds', signal).then(
      (payload) => payload.holds ?? [],
    ),

  createLegalHold: (input: CreateLegalHoldRequest) =>
    apiPost<{ id: Uuid }>('/admin/legal-holds', input),

  releaseLegalHold: (holdId: Uuid) => apiPost<{ ok: true }>(`/admin/legal-holds/${holdId}/release`),

  /* ---- eDiscovery export -------------------------------------------------- */

  listExports: (signal?: AbortSignal) =>
    apiGet<{ exports: ExportJob[] }>('/admin/exports', signal).then(
      (payload) => payload.exports ?? [],
    ),

  createExport: (input: CreateExportRequest) =>
    apiPost<{ id: Uuid; status: string }>('/admin/exports', input),
} as const;

/**
 * Self-service security — what a signed-in person may do to their OWN account.
 * Separate object because none of it needs an admin permission, and mixing the
 * two would invite a member surface to call an admin route.
 */
export const selfSecurityApi = {
  /**
   * READ `activeSessions`, NOT `sessions`.
   *
   * `GET /v1/auth/sessions` answers both: `sessions` is the narrow legacy
   * `Session` shape kept for older clients, and `activeSessions` is the rich
   * `ActiveSession` with deviceName, location, lastSeenAt and mfaSatisfied.
   * Reading `sessions` compiles happily and then renders a card full of blanks,
   * which is exactly the bug this comment exists to prevent.
   */
  listSessions: (signal?: AbortSignal) =>
    apiGet<{ sessions: unknown[]; activeSessions: ActiveSession[] }>(
      '/auth/sessions',
      signal,
    ).then((payload) => payload.activeSessions ?? []),

  revokeSession: (sessionId: Uuid) => apiDelete<{ ok: true }>(`/auth/sessions/${sessionId}`),

  /** Ends every session except the one making the call. */
  revokeOtherSessions: () => apiPost<{ revoked: number }>('/auth/sessions/revoke-all'),

  enrolMfa: () => apiPost<MfaEnrolment>('/auth/mfa/enroll'),

  activateMfa: (code: string) =>
    apiPost<{ enabled: true; recoveryCodes: string[] }>('/auth/mfa/activate', { code }),

  disableMfa: (code: string) => apiPost<{ enabled: false }>('/auth/mfa/disable', { code }),

  regenerateRecoveryCodes: () => apiPost<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes'),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiPost<{ ok: true }>('/auth/password', { currentPassword, newPassword }),
} as const;

export type SecurityApi = typeof securityApi;
export type SelfSecurityApi = typeof selfSecurityApi;
